import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const LS_DEVICE_ID = 'feron_device_id';
const LS_USER = 'feron_user';

const genId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const getUserId = () => {
  // Prefer logged-in user email; else stable device id
  try {
    const u = JSON.parse(localStorage.getItem(LS_USER) || 'null');
    if (u && u.email) return u.email;
  } catch {}
  try {
    let id = localStorage.getItem(LS_DEVICE_ID);
    if (!id) {
      id = genId();
      localStorage.setItem(LS_DEVICE_ID, id);
    }
    return id;
  } catch {
    return 'anon';
  }
};

const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Read the CSRF cookie set by the backend after login/register/me.
const readCsrfCookie = () => {
  try {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const CSRF_METHODS = new Set(['post', 'put', 'patch', 'delete']);

api.interceptors.request.use((config) => {
  // Cookie session is primary; keep X-User-Id as fallback for anonymous cart/wishlist
  config.headers['X-User-Id'] = getUserId();
  // Double-submit CSRF: echo the cookie value on state-changing requests
  const method = (config.method || 'get').toLowerCase();
  if (CSRF_METHODS.has(method)) {
    const token = readCsrfCookie();
    if (token) config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

export default api;

// --- Products -----
export const fetchProducts = (params = {}) => api.get('/products', { params }).then((r) => r.data);
export const fetchProduct = (id) => api.get(`/products/${id}`).then((r) => r.data);
export const fetchReels = (limit = 6) => api.get('/products/reels', { params: { limit } }).then((r) => r.data);

// --- Cart -----
export const fetchCart = () => api.get('/cart').then((r) => r.data);
export const apiAddToCart = (productId, size, qty = 1) =>
  api.post('/cart', { productId, size, qty }).then((r) => r.data);
export const apiUpdateCart = (itemId, qty) =>
  api.put(`/cart/${itemId}`, { qty }).then((r) => r.data);
export const apiRemoveCart = (itemId) => api.delete(`/cart/${itemId}`).then((r) => r.data);

// --- Wishlist -----
export const fetchWishlist = () => api.get('/wishlist').then((r) => r.data);
export const apiToggleWishlist = (productId) =>
  api.post(`/wishlist/${productId}`).then((r) => r.data);

// --- Reviews -----
export const fetchReviews = (productId) =>
  api.get(`/products/${productId}/reviews`).then((r) => r.data);
export const apiPostReview = (productId, payload) =>
  api.post(`/products/${productId}/reviews`, payload).then((r) => r.data);

// --- Orders -----
export const apiPlaceOrder = (payload) => api.post('/orders', payload).then((r) => r.data);
export const fetchOrders = () => api.get('/orders').then((r) => r.data);

// --- Auth -----
export const authRegister = (email, password, name) =>
  api.post('/auth/register', { email, password, name }).then((r) => r.data);
export const authLogin = (email, password) =>
  api.post('/auth/login', { email, password }).then((r) => r.data);
export const authLogout = () => api.post('/auth/logout').then((r) => r.data);
export const authMe = () => api.get('/auth/me').then((r) => r.data);
export const authGoogleSession = (sessionId) =>
  api.post('/auth/google/session', { session_id: sessionId }).then((r) => r.data);

// --- Coupons -----
export const validateCoupon = (code, subtotal, currency) =>
  api.post('/coupons/validate', { code, subtotal, currency }).then((r) => r.data);

// --- Taxonomies (public read) -----
export const fetchTaxonomies = (kind) =>
  api.get('/taxonomies', { params: kind ? { kind } : {} }).then((r) => r.data);
